import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, initMultiMapFactory, queryMapFactory } from '../common/reducers/utilities';
import deepmerge from 'deepmerge';
import { deepEquals, readField, writeField } from '../common/utils/objectUtilities';
import { distinct, ensureArray } from '../common/utils/arrayUtilities';
import useClientConfig from '../common/hooks/clientConfigHook';
import { aggregatingFetch } from '../common/hooks/networkHook';
import { parseDate } from '../common/utils/dateUtils';
import { createEagerSelector, eagerMetaFactory } from '../common/reducers/pendingUpdateSlice';
import { deepClone } from "../common/utils/objectUtilities";
import { credentialsFactory, validateAccess } from '../utils/accessUtilities';
import { complexTableQuery } from '../common/utils/tableUtilities';

export const processJob = (job) => {
    if (job.displayid) {
        if (typeof job.displayid === 'string' && !isNaN(parseInt(job.displayid))) {
            job.displayid = parseInt(job.displayid);
        }
    }
    return job;
};

const resetNewJobThunk = createAsyncThunk(
    'job/resetNewJob',
    async (_, thunkAPI) => {
        const clientConfig = thunkAPI.getState().clientConfig.config;
        const defaultJob = JSON.parse(JSON.stringify(clientConfig?.jobDefault ?? {}));
        const credentials = credentialsFactory(thunkAPI.getState().auth.token);
        const access = credentials.access.accessTriples(clientConfig, 'full');
        if (access) {
            const accessSites = distinct(access.map(a => a.site))
            const accessLevels = distinct(access.map(a => a.level));
            const accessTypes = distinct(access.map(a => a.type));
            if (clientConfig.enableSites && !defaultJob.site && accessSites.length === 1) {
                defaultJob.site = accessSites[0];
            }
            if (clientConfig.enableLevels && !defaultJob.level && accessLevels.length === 1) {
                defaultJob.level = accessLevels[0]
            }
            if (!defaultJob.type && accessTypes.length === 1) {
                defaultJob.type = accessTypes[0];
            }
        }
        return defaultJob;
    }
)

export const resetNewJob = network => network.dispatch(resetNewJobThunk());

export const jobSlice = createSlice({
    name: 'job',
    initialState: {
        data: {},
        new: {},
        activeJob: null
    },
    reducers: {
        setActiveJob: (state, action) => {
            state.activeJob = action.payload
        },
        setActiveJobs: (state, action) => {
            const toRemove = new Set(Object.values(state.data).filter(j => !j.closed).map(j => j.jobid));
            for (const job of ensureArray(action.payload).map(processJob)) {
                const id = job.jobid;
                if (id) {
                    if (!state.data[id] || !deepEquals(state.data[id], job)) {
                        state.data[id] = job;
                    }
                } else {
                    state.new = deepmerge(state.new, job, { arrayMerge: (dst, src, options) => src });
                }
                toRemove.delete(id);
            }
            for (const jobid of toRemove) {
                if (state.activeJob !== jobid) {
                    delete state.data[jobid]
                }
            }
        },
        addOrUpdate: (state, action) => {
            for (const job of ensureArray(action.payload).map(processJob)) {
                const id = job.jobid?.toString();
                if (id) {
                    if (!state.data[id] || !deepEquals(state.data[id], job)) {
                        state.data[id] = job;
                    }
                } else {
                    state.new = deepmerge(state.new, job, { arrayMerge: (dst, src, options) => src });
                }
            }
        },
        remove: (state, action) => {
            for (const job of ensureArray(action.payload)) {
                const id = job.jobid?.toString();
                if (state.data[id]) {
                    delete state.data[id];
                }
                if (state.activeJob?.toString() == id) {
                    state.activeJob = null;
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => jobSlice.getInitialState());
        builder.addCase(resetNewJobThunk.fulfilled, (state, action) => {
            state.new = deepClone(action.payload ?? {});
        })
    }
});

// #region network operations
const eagerMeta = eagerMetaFactory(jobSlice.name);
const fetchActiveJobs = network => network.get('/api/v1/activeJobs', jobSlice.actions.setActiveJobs);
const fetchJob = aggregatingFetch(
    jobid => network => {
        return network.get('/api/v1/job/' + encodeURIComponent(jobid), jobSlice.actions.addOrUpdate)
    },
    jobids => network => {
       return  network.post('/api/v1/jobsById', ensureArray(jobids), jobSlice.actions.addOrUpdate)
    },
    { primitiveKey: true }
)
const fetchReferencedJobs = (field, value) => {
    // we do field directly so we end up with customfields.foo.bar so that we do a path search on the serverside rather than matching structure
    return network => network.post('/api/v1/job/search', { [field]: value }, jobSlice.actions.addOrUpdate);
}
export const searchJobs = search => network => network.post('/api/v1/job/search', search, jobSlice.actions.addOrUpdate);
export const updateJob = (network, delta) => {
    if (delta.jobid) {
        return network.put('/api/v1/job', delta, jobSlice.actions.addOrUpdate, eagerMeta(delta.jobid));
    } else {
        network.dispatch(jobSlice.actions.addOrUpdate(delta));
    }
}
export const createJob = (network, job) => {
    let data = { job: { ...job } };
    if (job.assistance) {
        data.assistance = job.assistance;
        delete data.job.assistance;
    }
    return network.post('/api/v1/job', data, jobSlice.actions.addOrUpdate, eagerMeta(data.jobid));
}
export const continueJob = (network, data) => network.post('/api/v1/continueJob', data, jobSlice.actions.addOrUpdate);

export const getJobActions = async (network, jobid) => {
    const res = await network.fetchData('/api/v1/jobActions/' + encodeURIComponent(jobid));
    if (res.ok) {
        return await res.json();
    }
    return [];
}
export const doJobAction = (network, jobid, actionid, reason) => network.post('/api/v1/jobAction', { jobid, actionid, origin: 'Chronicler', reason });
export const updateJobDefault = (network, username, defaults) => network.post('/api/v1/setting', { username, key: 'jobDefault', value: defaults});
// #endregion

export const setActiveJob = (dispatch, jobid) => {
    dispatch(jobSlice.actions.setActiveJob(jobid))
};
export const selectActiveJob = state => state.job.activeJob;

// #region selectors
const eagerSelector = createEagerSelector(jobSlice.name, { mode: 'map', key: job => job.jobid});
const selectAllJobsBase = state => state.job.data;
const selectAllJobs = eagerSelector(selectAllJobsBase);
//const selectAllJobs = state => state.job.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectNewJob = state => state.job.new;
const selectActiveJobs = createSelector([selectAllJobs, (state, value) => value], jobs => Object.values(jobs).filter(j => j.jobid && j.status !== 'Closed'));

const selectJobByID = createSelector([selectAllJobs, (state, jobid) => jobid], (jobs, jobid) => jobs[jobid.toString()] ?? {});
const selectJob = jobid => state => jobid ? selectJobByID(state, jobid) : selectNewJob(state);
const selectJobsByID = createSelector([selectAllJobs, (state, jobids) => jobids], (jobs, jobids) => jobids.map(jobid => jobs[jobid.toString()]).filter(job => job));
const selectJobs = jobids => state => selectJobsByID(state, jobids);

const selectJobSearchResults = search => createSelector([selectAllJobs], jobs => {
    return Object.values(jobs).filter(j => (!search.start || parseDate(search.start) <= parseDate(j.created)) && (!search.end || parseDate(j.created) < parseDate(search.end)));
});
const selectReferencedJobs = (field, value) => createSelector([selectAllJobs], jobs => {
    const query = {};
    writeField(query, field, value);
    return complexTableQuery(Object.values(jobs), query);
})
// #endregion

// #region listeners
const listen = {
    jobCreated: function jobCreatedListener (job, { dispatch }) { dispatch(jobSlice.actions.addOrUpdate(job)) },
    jobUpdated: function jobUpdatedListener (job, { dispatch }) { dispatch(jobSlice.actions.addOrUpdate(job)) },
    jobRemoved: async function jobRemovedListener  (job, { dispatch }) {
        dispatch(jobSlice.actions.remove(job));
    }
}
// #endregion

// #region utilities
export function getDescriptor(descriptorDef, job) {
    return descriptorDef?.map((d, i) => {
        let toReturn = '';
        let data = null;
        if (d.field) {
            data = readField(job, d.field);
        }
        if (data || !d.field) {
            if (i > 0 && d.separator) {
                toReturn += d.separator;
            }
            if (d.prefix) {
                toReturn += d.prefix;
            }
            if (data) {
                toReturn += data;
            }
            if (d.suffix) {
                toReturn += d.suffix;
            }
        }
        return toReturn;
    })?.join('') ?? '';
}
// #endregion

// #region permission checks
export const canInsertJob = (credentials, clientConfig, job) => credentials.access.hasFull({ type: job.type, site: job.site, level: job.level });
export const canUpdateJob = (credentials, clientConfig, job, assistance) => {
    for (const effectiveAssistance of ensureArray(clientConfig.effectiveAssistance).filter(effectiveAssistance => effectiveAssistance?.enable)) {
        if (!effectiveAssistance?.matchTemplate || Object.entries(effectiveAssistance.matchTemplate).every(([field, value]) => readField(job, field) === value)) {
            return validateAccess(credentials, effectiveAssistance.assistance ?? assistance) || (!effectiveAssistance?.overridePrimary && credentials.access.hasFull({ type: job.type, site: job.site, level: job.level }));
        }
    }
    return validateAccess(credentials, assistance) || credentials.access.hasFull({ type: job.type, site: job.site, level: job.level });
}
export const canRemoveJob = canInsertJob;
export const canLogJob = (credentials, clientConfig, job, assistance) => {
    for (const effectiveAssistance of ensureArray(clientConfig.effectiveAssistance).filter(effectiveAssistance => effectiveAssistance?.enable)) {
        if (!effectiveAssistance?.matchTemplate || Object.entries(effectiveAssistance.matchTemplate).every(([field, value]) => readField(job, field) === value)) {
            return validateAccess(credentials, effectiveAssistance.assistance ?? assistance, { level: 2 }) || (!effectiveAssistance?.overridePrimary && credentials.access.hasLog({ type: job.type, site: job.site, level: job.level }));
        }
    }
    return validateAccess(credentials, assistance, { level: 2 }) || credentials.access.hasLog({ type: job.type, site: job.site, level: job.level });
}
export const canAccessJob = (credentials, clientConfig, job, assistance) => validateAccess(credentials, assistance) || credentials.access.has({ type: job.type, site: job.site, level: job.level });
// #endregion

export const jobIsValid = (clientConfig, job, requiredFields) => (!clientConfig.enableSites || job?.site) && (!clientConfig.enableLevels || job?.level) && job?.type && job?.problem && job?.priority && job?.location && requiredFields.every(f => readField(job, f) !== undefined && readField(job, f) !== null);

export const selectAllNonActive = state => {
    const activeJobID = selectActiveJob(state);
    const jobs = Object.values(selectAllJobs(state)).filter(j => j.jobid?.toString() !== activeJobID?.toString());
    return jobs;
}

// this clean-up is a bit special - we always want to keep active jobs alive so we only select non-active jobs for clean-up consideration here
const jobInitByID = initMapFactory(fetchJob, listen, { name: 'jobById' });
const jobCleanup = { cleanup: { root: 'job', all: selectAllNonActive, remove: jobSlice.actions.remove, removeAll: jobSlice.actions.remove } };

export const [useJob, realizeJob] = dataHookFactory(selectJob, jobInitByID, jobCleanup);
export const [useJobs, realizeJobs] = dataHookFactory(selectJobs, initMultiMapFactory(jobInitByID, listen), jobCleanup);
// Note - we invoke the queryMapFactory because the selector takes no arguments and so the initHook will not invoke to produce the init, similarly we need to allowEmpty
export const [useActiveJobs] = dataHookFactory(selectActiveJobs, queryMapFactory(fetchActiveJobs, 'jobid', jobInitByID, listen, { allowEmpty: true })(), jobCleanup);
export const [useJobSearch, realizeJobSearch] = dataHookFactory(selectJobSearchResults, queryMapFactory(searchJobs, 'jobid', jobInitByID, listen), jobCleanup);
export const [useReferencedJobs] = dataHookFactory(selectReferencedJobs, queryMapFactory(fetchReferencedJobs, 'jobid', jobInitByID, listen), jobCleanup);

const EMPTY_JOB_DEFAULT = {};
export const useJobDefault = () => {
    const clientConfig = useClientConfig();
    return clientConfig.jobDefault ?? EMPTY_JOB_DEFAULT;
}

const reducerDetails = { name: jobSlice.name, reducer: jobSlice.reducer };
export default reducerDetails;